import React, { useEffect } from 'react';
import { useAuthContext } from '../components/AuthProvider';

/**
 * Higher-order component that adds authentication checks to a component
 * 
 * @param Component - The component to wrap with authentication checking
 * @returns Wrapped component with authentication checking
 */
export function withAuth<P extends object>(
  Component: React.FC<P>
): React.FC<P> {
  const WithAuthComponent: React.FC<P> = (props) => {
    const { refreshAuth, lastChecked } = useAuthContext();
    
    // Check authentication if it hasn't been checked in the last 5 minutes
    useEffect(() => {
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (now - lastChecked > fiveMinutes) {
        refreshAuth();
      }
    }, [lastChecked, refreshAuth]);
    
    return <Component {...props} />;
  };
  
  // Set display name for debugging
  const displayName = Component.displayName || Component.name || 'Component';
  WithAuthComponent.displayName = `withAuth(${displayName})`;
  
  return WithAuthComponent;
}

export default withAuth; 